import React, {useState, useMemo, useEffect} from 'react';
import {ScrollView, StyleSheet, Pressable, Linking} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCTextInput from '@/Atoms/CCTextInput';
import CCColors from '@/Utils/CCColors';
import {FontSizes, vs, hs} from '@/Utils/Dimensions';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import {useNavigation, useRoute} from '@react-navigation/native';
import {StackActions} from '@react-navigation/native';
import {ScreenName} from '@/navigators/StackConstants';

const CSOC_NEW_PAYMENT_PATH = 'csoc-new-payment';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import {formatCurrency, getUserDetails} from '@/Utils/CommonUtils';
import useCreateRenewalOrder from '@/Hooks/RenewalApi/useCreateRenewalOrder';
import CCLoader from '@/Atoms/CCLoader';
import Spacer from '@/Atoms/Spacer';
import CouponModal from './CouponModal';
import InternationalPaymentMethodSelector, {
  InternationalPaymentMethod,
} from '@/Components/InternationalPaymentMethodSelector';

function getQueryParam(name: string): string | null {
  if (typeof window === 'undefined' || !window.location?.search) {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  return params.get(name);
}

interface RenewalPaymentScreenProps {}

const RenewalPaymentScreen: React.FC<RenewalPaymentScreenProps> = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const {isMobileOrTab} = useMediaQuery();
  const [couponCode, setCouponCode] = useState<string>('');
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const [isCheckingCoupon, setIsCheckingCoupon] = useState(false);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [paymentMethod, setPaymentMethod] =
    useState<InternationalPaymentMethod>();
  const [billingFirstName, setBillingFirstName] = useState('');
  const [billingLastName, setBillingLastName] = useState('');
  const [billingLine1, setBillingLine1] = useState('');
  const [billingCountry, setBillingCountry] = useState('');

  const levelFromUrl = getQueryParam('level');
  const paramsFromRoute = route.params || {};
  const {
    renewalData: renewalDataFromParams,
    selectedPlanType: selectedPlanTypeFromParams,
    selectedDuration: selectedDurationFromParams,
    selectedPricing: selectedPricingFromParams,
    selectedSlots = [],
    totalSessions,
  } = paramsFromRoute as any;

  // New-user flow: redirect to Renewal so they see the exact same UI (PlanSelector, DurationSelector, etc.)
  const hasLevelParam = Boolean(levelFromUrl);
  const hasSelectionFromRenewal = Boolean(
    renewalDataFromParams && selectedPricingFromParams,
  );
  useEffect(() => {
    if (!hasLevelParam || hasSelectionFromRenewal) {
      return;
    }
    // On web, in-app navigation doesn't update the URL so we stay on RenewalPayment and the loader never leaves.
    // Do a full redirect so the URL becomes /Renewal?level=... and the Renewal screen loads.
    if (typeof window !== 'undefined' && window.location) {
      const path = window.location.pathname.replace(
        /RenewalPayment/,
        CSOC_NEW_PAYMENT_PATH,
      );
      const url = `${window.location.origin}${path}?level=${encodeURIComponent(
        levelFromUrl,
      )}`;
      window.location.replace(url);
      return;
    }
    const nav = navigation as any;
    if (typeof nav.replace === 'function') {
      nav.replace(ScreenName.CsocNewPayment, {level: levelFromUrl});
    } else if (typeof nav.dispatch === 'function') {
      nav.dispatch(
        StackActions.replace(ScreenName.CsocNewPayment, {level: levelFromUrl}),
      );
    } else {
      nav.navigate(ScreenName.CsocNewPayment, {level: levelFromUrl});
    }
  }, [hasLevelParam, hasSelectionFromRenewal, levelFromUrl, navigation]);

  if (hasLevelParam && !hasSelectionFromRenewal) {
    return (
      <Box flex={1} justifyContent="center" alignItems="center">
        <CCLoader loading={true} />
      </Box>
    );
  }

  const renewalData = renewalDataFromParams;
  const selectedPricing = selectedPricingFromParams;
  const selectedDuration = selectedDurationFromParams;
  const selectedPlanType = selectedPlanTypeFromParams;
  const isNewUserFlow = false;

  const {mutate: createRenewalOrder, isLoading: isCreatingOrder} =
    useCreateRenewalOrder(
      data => {
        // Handle successful payment link generation
        const paymentLink =
          data?.data?.payment_link || data?.content?.payment_links?.web;
        if (paymentLink) {
          Linking.openURL(paymentLink);
        }
      },
      error => {
        console.error('Error creating renewal order:', error);
      },
    );

  // Calculate pricing breakdown using new price_per_session structure (or roadmap for webinar)
  const pricingBreakdown = useMemo(() => {
    if (!selectedPricing) {
      return null;
    }
    const currency =
      selectedPricing.currency_code || selectedPricing.currency || 'INR';
    const roadmap = (selectedPricing.roadmaps || []).find(
      (r: any) => r.duration_months === selectedDuration,
    );
    const useRoadmap = roadmap && !roadmap.incomplete && roadmap.total_price;

    const pricePerSession = parseFloat(
      selectedPricing.price_per_session ||
        selectedPricing.per_session_price ||
        '0',
    );
    const maxPricePerSession = parseFloat(
      selectedPricing.max_price_per_session ||
        selectedPricing.price_per_session ||
        '0',
    );
    if (!useRoadmap && !pricePerSession && !maxPricePerSession) {
      return null;
    }
    const gstRate = 0.18; // 18% GST
    const sessionsPerMonth = 8;
    const totalSessionsForDuration = sessionsPerMonth * selectedDuration;
    const maxBasePriceExclGst = useRoadmap
      ? parseFloat(roadmap.max_total_price || roadmap.total_price || '0')
      : maxPricePerSession * totalSessionsForDuration;
    const offerPriceExclGst = useRoadmap
      ? parseFloat(roadmap.total_price || '0')
      : pricePerSession * totalSessionsForDuration;
    const offerDiscountAmount = 0;
    const offerDiscountPercentage = 0;
    const basePrice = maxBasePriceExclGst;

    // Price already includes GST, so calculate GST amount for display purposes
    // GST = Price × (GST Rate / (1 + GST Rate))
    const gstAmount =
      currency === 'USD' ? 0 : basePrice * (gstRate / (1 + gstRate));

    // Early renewal discount (not available in new structure, set to 0)
    const earlyRenewalDiscountPct = 0;
    const earlyRenewalDiscount = 0;

    // Calculate coupon discount (applied to max price)
    let couponDiscount = 0;
    if (appliedCoupon) {
      if (appliedCoupon.discount_type === 'percentage') {
        couponDiscount =
          (basePrice * parseFloat(appliedCoupon.discount_value)) / 100;
      } else {
        couponDiscount = parseFloat(appliedCoupon.discount_value);
      }
    }

    // Calculate subtotal after discounts (max price - coupon discount)
    const subtotal = basePrice - earlyRenewalDiscount - couponDiscount;

    // Grand total
    const grandTotal = subtotal;

    // Monthly equivalent
    const monthlyEquivalent = grandTotal / selectedDuration;

    return {
      maxBasePrice: maxBasePriceExclGst,
      basePrice,
      offerPrice: offerPriceExclGst, // Hidden from display
      offerDiscountAmount,
      offerDiscountPercentage,
      earlyRenewalDiscount,
      earlyRenewalDiscountPct,
      couponDiscount,
      subtotal,
      gstAmount,
      grandTotal,
      monthlyEquivalent,
      currency,
    };
  }, [selectedPricing, appliedCoupon, selectedDuration, selectedPlanType]);

  // Auto-apply coupon from renewal data
  React.useEffect(() => {
    if (renewalData?.coupon && !appliedCoupon) {
      setAppliedCoupon(renewalData.coupon);
      setCouponCode(renewalData.coupon.code);
    }
  }, [renewalData]);

  const handleApplyCoupon = (coupon: any) => {
    setAppliedCoupon(coupon);
    setCouponCode(coupon.code);
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const handlePayNow = async () => {
    if (!renewalData || !selectedPricing || !pricingBreakdown) {
      return;
    }

    const user = renewalData.user;
    const returnUrl =
      typeof window !== 'undefined'
        ? `${window.location.origin}/renewal/success`
        : '';
    const userDetailsRaw = await getUserDetails();
    const userDetails =
      typeof userDetailsRaw === 'string'
        ? (JSON.parse(userDetailsRaw) as {player?: number})
        : (userDetailsRaw as {player?: number});
    const player_id = userDetails?.player;
    const tournamentId =
      selectedPricing?.tournament_id ??
      (renewalData as any).recommended_next_level?.tournament_id ??
      (renewalData as any).current_subscription?.tournament_id;
    createRenewalOrder({
      tournament_id: tournamentId,
      plan_type: selectedPlanType,
      duration_months: selectedDuration,
      coupon_code: appliedCoupon?.code,
      selectedSlots: selectedSlots,
      customer_id: user.id,
      player_id: player_id,
      ...(isNewUserFlow ? {player_name: user.name} : {}),
      mobile_number: user.mobile,
      email: user.email || `${user.mobile}@circlechess.com`,
      return_url: returnUrl,
      amount: pricingBreakdown.grandTotal,
      currency: pricingBreakdown.currency,
      selected_pricing_id: selectedPricing.id,
      category_str: `${
        pricingBreakdown?.currency ?? 'INR'
      } /${selectedDuration}-Months`,
      isNewUser: isNewUserFlow,
      ...(pricingBreakdown.currency !== 'INR'
        ? {
            billing_address_first_name: billingFirstName.trim(),
            billing_address_last_name: billingLastName.trim(),
            billing_address_line1: billingLine1.trim(),
            billing_address_country: billingCountry.trim(),
            payment_method_type: paymentMethod,
          }
        : {}),
    });
  };

  if (!renewalData || !selectedPricing || !pricingBreakdown) {
    return (
      <Box flex={1} justifyContent="center" alignItems="center">
        <CCText>Loading...</CCText>
      </Box>
    );
  }

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}>
      {/* Header */}
      <Box
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        padding={20}>
        <Box flexDirection="row" alignItems="center" gap={10}>
          <CCText
            fontSize={FontSizes[24]}
            fontWeight="bold"
            color={CCColors.TextColor.Dark}>
            CircleChess
          </CCText>
        </Box>
        <Pressable onPress={() => navigation.goBack()}>
          <MaterialCommunityIcons
            name="phone"
            size={24}
            color={CCColors.TextColor.Dark}
          />
        </Pressable>
      </Box>

      <Box paddingHorizontal={20}>
        <CCText
          fontSize={FontSizes[28]}
          fontWeight="bold"
          color={CCColors.TextColor.Dark}
          marginBottom={30}>
          Pay Now
        </CCText>

        {/* Coupon Code Section */}
        <Box
          backgroundColor={CCColors.White}
          padding={20}
          borderRadius={8}
          marginBottom={20}>
          <Box
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            marginBottom={10}>
            <CCText fontSize={FontSizes[16]} fontWeight="semibold">
              Coupon Code
            </CCText>
            <Pressable onPress={() => setShowCouponModal(true)}>
              <Box flexDirection="row" alignItems="center" gap={6}>
                <MaterialCommunityIcons
                  name="ticket-percent"
                  size={16}
                  color={CCColors.Primary.Default}
                />
                <CCText
                  fontSize={FontSizes[14]}
                  color={CCColors.Primary.Default}
                  fontWeight="semibold">
                  View All Coupons
                </CCText>
              </Box>
            </Pressable>
          </Box>
          {appliedCoupon ? (
            <Box
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              padding={12}
              backgroundColor={CCColors.Primary.lightBrown}
              borderRadius={8}
              borderWidth={1}
              borderColor={CCColors.Green}>
              <Box flexDirection="row" alignItems="center" gap={8}>
                <MaterialCommunityIcons
                  name="check-circle"
                  size={20}
                  color={CCColors.Green}
                />
                <Box>
                  <CCText
                    fontSize={FontSizes[14]}
                    fontWeight="semibold"
                    color={CCColors.TextColor.Dark}>
                    {appliedCoupon.code}
                  </CCText>
                  <CCText
                    fontSize={FontSizes[12]}
                    color={CCColors.TextColor.Grey}>
                    {appliedCoupon.discount_type === 'percentage'
                      ? `${appliedCoupon.discount_value}% off`
                      : `${formatCurrency(
                          parseFloat(appliedCoupon.discount_value),
                          appliedCoupon.currency || 'INR',
                        )} off`}
                  </CCText>
                </Box>
              </Box>
              <Pressable onPress={handleRemoveCoupon}>
                <MaterialCommunityIcons
                  name="close-circle"
                  size={20}
                  color={CCColors.Red}
                />
              </Pressable>
            </Box>
          ) : (
            <Box flexDirection="row" gap={10}>
              <Box flex={1}>
                <CCTextInput
                  placeholder="Enter code"
                  value={couponCode}
                  onChangeText={setCouponCode}
                  style={styles.couponInput}
                />
              </Box>
              <CCButton
                onPress={() => setShowCouponModal(true)}
                style={styles.checkButton}
                contentContainerStyle={styles.checkButtonContent}>
                <MaterialCommunityIcons
                  name="ticket-percent"
                  size={16}
                  color={CCColors.TextColor.Dark}
                />
              </CCButton>
            </Box>
          )}
        </Box>

        {pricingBreakdown.currency !== 'INR' && (
          <Box
            backgroundColor={CCColors.White}
            padding={20}
            borderRadius={8}
            marginBottom={20}>
            <InternationalPaymentMethodSelector
              value={paymentMethod}
              onChange={setPaymentMethod}
            />
            <CCText
              fontSize={FontSizes[14]}
              fontWeight="semibold"
              marginBottom={10}>
              Billing details
            </CCText>
            <CCTextInput
              placeholder="First name"
              value={billingFirstName}
              onChangeText={setBillingFirstName}
              style={styles.billingInput}
            />
            <CCTextInput
              placeholder="Last name"
              value={billingLastName}
              onChangeText={setBillingLastName}
              style={styles.billingInput}
            />
            <CCTextInput
              placeholder="Address"
              value={billingLine1}
              onChangeText={setBillingLine1}
              style={styles.billingInput}
            />
            <CCTextInput
              placeholder="Country code, for example US"
              value={billingCountry}
              onChangeText={setBillingCountry}
              style={styles.billingInput}
            />
          </Box>
        )}

        {/* Bill Details */}
        <Box
          backgroundColor={CCColors.White}
          padding={20}
          borderRadius={8}
          marginBottom={20}>
          <CCText
            fontSize={FontSizes[16]}
            fontWeight="semibold"
            marginBottom={15}>
            Bill Details
          </CCText>

          <Box
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            marginBottom={10}>
            <Box flex={1}>
              <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Dark}>
                Base Fee
              </CCText>
              <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Grey}>
                ({selectedDuration} Month Plan)
              </CCText>
            </Box>
            <Box alignItems="flex-end">
              <Box flexDirection="row" alignItems="center" gap={8}>
                <CCText
                  fontSize={FontSizes[14]}
                  fontWeight="semibold"
                  color={CCColors.TextColor.Dark}>
                  {formatCurrency(
                    pricingBreakdown.maxBasePrice || pricingBreakdown.basePrice,
                    pricingBreakdown.currency,
                  )}
                </CCText>
                {pricingBreakdown.offerDiscountPercentage &&
                pricingBreakdown.offerDiscountPercentage > 0 ? (
                  <Box
                    backgroundColor={CCColors.Orange}
                    paddingHorizontal={6}
                    paddingVertical={2}
                    borderRadius={4}>
                    <CCText
                      fontSize={FontSizes[10]}
                      color={CCColors.White}
                      fontWeight="bold">
                      OFFER
                    </CCText>
                  </Box>
                ) : (
                  <></>
                )}
              </Box>
            </Box>
          </Box>

          {pricingBreakdown.earlyRenewalDiscount > 0 ? (
            <Box
              flexDirection="row"
              justifyContent="space-between"
              marginBottom={10}>
              <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Dark}>
                Early renewal discount
              </CCText>
              <CCText
                fontSize={FontSizes[14]}
                fontWeight="semibold"
                color={CCColors.Green}>
                -
                {formatCurrency(
                  pricingBreakdown.earlyRenewalDiscount,
                  pricingBreakdown.currency,
                )}
              </CCText>
            </Box>
          ) : (
            <></>
          )}

          {pricingBreakdown.couponDiscount > 0 ? (
            <Box
              flexDirection="row"
              justifyContent="space-between"
              marginBottom={10}>
              <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Dark}>
                Coupon discount
              </CCText>
              <CCText
                fontSize={FontSizes[14]}
                fontWeight="semibold"
                color={CCColors.Green}>
                -
                {formatCurrency(
                  pricingBreakdown.couponDiscount,
                  pricingBreakdown.currency,
                )}
              </CCText>
            </Box>
          ) : (
            <></>
          )}

          <Box
            height={1}
            backgroundColor={CCColors.BorderBottom}
            marginVertical={15}
          />

          <Box
            flexDirection="row"
            justifyContent="space-between"
            marginBottom={10}>
            <Box flex={1}>
              <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Dark}>
                GST (18%)
              </CCText>
            </Box>
            <CCText
              fontSize={FontSizes[14]}
              fontWeight="semibold"
              color={CCColors.TextColor.Dark}>
              {formatCurrency(
                pricingBreakdown.gstAmount,
                pricingBreakdown.currency,
              )}
            </CCText>
          </Box>

          <Box
            height={1}
            backgroundColor={CCColors.BorderBottom}
            marginVertical={15}
          />

          <Box flexDirection="row" justifyContent="space-between">
            <Box flex={1}>
              <CCText
                fontSize={FontSizes[16]}
                fontWeight="bold"
                color={CCColors.TextColor.Dark}>
                Grand Total
              </CCText>
              <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Grey}>
                (
                {formatCurrency(
                  pricingBreakdown.monthlyEquivalent,
                  pricingBreakdown.currency,
                )}{' '}
                Monthly)
              </CCText>
            </Box>
            <CCText
              fontSize={FontSizes[18]}
              fontWeight="bold"
              color={CCColors.TextColor.Dark}>
              {formatCurrency(
                pricingBreakdown.grandTotal,
                pricingBreakdown.currency,
              )}
            </CCText>
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        padding={20}
        backgroundColor={CCColors.TextColor.Dark}
        style={{marginTop: 'auto'}}>
        <Box>
          <CCText
            fontSize={FontSizes[16]}
            fontWeight="semibold"
            color={CCColors.White}>
            {selectedPlanType === 'personal'
              ? 'Personal 1:1'
              : selectedPlanType === 'webinar'
              ? 'Webinar'
              : 'Small Group'}
          </CCText>
          <CCText fontSize={FontSizes[12]} color={CCColors.White}>
            {selectedPlanType === 'personal'
              ? 'One-on-one'
              : selectedPlanType === 'webinar'
              ? 'Webinar'
              : '4-6 Students'}{' '}
            | {selectedDuration} Months
          </CCText>
        </Box>
        <CCButton
          onPress={handlePayNow}
          disabled={
            isCreatingOrder ||
            (pricingBreakdown.currency !== 'INR' &&
              (!billingFirstName.trim() ||
                !billingLastName.trim() ||
                !billingLine1.trim() ||
                !billingCountry.trim()))
          }
          style={styles.payButton}
          contentContainerStyle={styles.payButtonContent}>
          {isCreatingOrder ? (
            <CCLoader loading={true} />
          ) : (
            <CCText
              fontSize={FontSizes[16]}
              fontWeight="semibold"
              color={CCColors.TextColor.Dark}>
              Pay{' '}
              {formatCurrency(
                pricingBreakdown.basePrice,
                pricingBreakdown.currency,
              )}
            </CCText>
          )}
        </CCButton>
      </Box>

      {/* Coupon Modal */}
      <CouponModal
        visible={showCouponModal}
        onClose={() => setShowCouponModal(false)}
        onApplyCoupon={handleApplyCoupon}
        tournamentId={
          selectedPricing?.tournament_id ??
          (renewalData as any).recommended_next_level?.tournament_id ??
          (renewalData as any).current_subscription?.tournament_id
        }
        planType={selectedPlanType}
        durationMonths={selectedDuration}
        appliedCouponCode={appliedCoupon?.code}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: CCColors.CCTableBg,
  },
  contentContainer: {
    paddingBottom: 100,
  },
  couponInput: {
    flex: 1,
  },
  billingInput: {
    marginBottom: 10,
  },
  checkButton: {
    backgroundColor: CCColors.LightYellow,
    borderRadius: 8,
    paddingHorizontal: 20,
  },
  checkButtonContent: {
    padding: 0,
  },
  payButton: {
    backgroundColor: CCColors.LightYellow,
    borderRadius: 8,
    paddingHorizontal: 30,
    paddingVertical: 12,
  },
  payButtonContent: {
    padding: 0,
  },
});

export default RenewalPaymentScreen;
